<template>
    <UPopover v-if="selectedUnit && units.length > 0" v-model:open="open" :disabled="!canChangeUnit">
        <UButton
            color="neutral"
            variant="ghost"
            :trailing-icon="canChangeUnit ? 'i-lucide-chevron-down' : undefined"
            :ui="{ base: 'max-w-72 sm:border sm:border-default sm:bg-default sm:shadow-xs', trailingIcon: 'text-primary size-4 stroke-[3]' }"
        >
            <AppIcon name="home" class="size-9" />
            <span class="hidden min-w-0 flex-col text-left sm:flex">
                <span class="text-[0.65rem] leading-none tracking-wide text-muted uppercase">
                    {{ $t('unit.label') }}
                </span>
                <span class="truncate text-sm leading-4 font-semibold whitespace-nowrap">
                    {{ displayUnit }}
                </span>
            </span>
        </UButton>

        <template #content>
            <div class="w-80 max-w-[calc(100vw-2rem)] p-2">
                <UInput
                    v-if="showSearch"
                    v-model="searchUnitsValue"
                    class="mb-2 w-full"
                    size="sm"
                    icon="i-lucide-search"
                    :placeholder="$t('unit.search')"
                    @update:model-value="searchUnits"
                />

                <ul class="flex max-h-72 flex-col gap-0.5 overflow-auto">
                    <li v-for="unit in unitsToDisplay" :key="unit.id_enote">
                        <button
                            type="button"
                            class="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-elevated"
                            :class="{ 'bg-primary/10 font-semibold': isSelected(unit) }"
                            @click="onSelectUnit(unit)"
                        >
                            <span class="min-w-0 flex-1">{{ unitText(unit) }}</span>
                            <UIcon v-if="isSelected(unit)" name="i-lucide-check" class="size-4 shrink-0 text-primary" />
                        </button>
                    </li>
                    <li v-if="unitsToDisplay.length === 0" class="px-3 py-2 text-sm text-muted">
                        {{ $t('manager.no_results') }}
                    </li>
                </ul>
            </div>
        </template>
    </UPopover>
</template>

<script setup lang="ts">
    import { computed, onMounted, ref, type Ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'

    import AppIcon from '@/components/AppIcon.vue'

    import { parseError } from '@/libs/error'
    import { unitText } from '@/libs/helpers'
    import { PrintError } from '@/libs/toast'
    import { useAppStore } from '@/stores/app'
    import { useFetchStore } from '@/stores/fetch'
    import type { Unit } from '@/types/Unit'

    // config
    const SEARCH_THRESHOLD = 7

    // data
    const { t } = useI18n()
    const appStore = useAppStore()
    const fetchStore = useFetchStore()

    const units: Ref<Unit[]> = ref([])

    const filteredUnits: Ref<Unit[]> = ref([])
    const searchUnitsValue: Ref<string> = ref('')
    let searchUnitsTimeout: ReturnType<typeof setTimeout> | undefined

    const open: Ref<boolean> = ref(false)

    // computed
    const loggedIn = computed(() => {
        return appStore.getUserLoggedIn()
    })

    const selectedUnit = computed(() => appStore.getSelectedUnit())

    const displayUnit = computed(() => {
        if (selectedUnit.value) return unitText(selectedUnit.value)
        return '/'
    })

    const unitsToDisplay = computed(() => {
        const base = searchUnitsValue.value ? filteredUnits.value : units.value
        const sel = selectedUnit.value

        let ordered = base
        if (sel) {
            const idx = base.findIndex(u => u.id_enote === sel.id_enote)
            if (idx > 0) {
                ordered = [...base]
                const [item] = ordered.splice(idx, 1)
                if (item) ordered.unshift(item)
            }
        }

        return searchUnitsValue.value ? ordered : ordered.slice(0, 10)
    })

    const canChangeUnit = computed(() => {
        return units.value.length > 1
    })

    const showSearch = computed(() => {
        return units.value.length > SEARCH_THRESHOLD
    })

    // methods
    const isSelected = (unit: Unit) => {
        return !!selectedUnit.value && selectedUnit.value.id_enote === unit.id_enote
    }

    const setUnits = (allUnits: Unit[]) => {
        units.value = []

        allUnits.forEach((x) => {
            const result = allUnits.find((element: Unit) => element.inventarna_stevilka === x.inventarna_stevilka && element.vrsta !== x.vrsta)

            if (x.vrsta === 'L') {
                if (result) {
                    x.vrsta = 'L+U'
                    units.value.push(x)
                } else {
                    units.value.push(x)
                }
            } else if (!result) {
                units.value.push(x)
            }
        })

        for (const unit of units.value) {
            unit.full_title = unitText(unit)
        }

        units.value = units.value.sort((a, b) => {
            return a.naziv.localeCompare(b.naziv)
                && Number(b.stev_stavbe) - Number(a.stev_stavbe)
                && b.zaporedna - a.zaporedna
                && b.stev_stanovanja - a.stev_stanovanja
        })

        filteredUnits.value = units.value.slice(0, 10)

        appStore.setUnits(units.value)

        if (units.value.length === 0) {
            appStore.setSelectedUnit(undefined)
        }

        const currentSelectedUnit = appStore.getSelectedUnit()
        if (!currentSelectedUnit && units.value.length > 0) {
            appStore.setSelectedUnit(units.value[0])
        }
    }

    const loadData = async() => {
        if (!loggedIn.value) return
        appStore.startLoading()
        const { response, data, error } = await fetchStore.fetch('/api/iiportal-user/v1/e_manager/enota').get().json()
        if (response.value?.status === 200 && data.value) {
            const allUnits = data.value ?? []

            appStore.setRawUnits(JSON.parse(JSON.stringify(data.value ?? [])))
            setUnits(allUnits)
        } else {
            PrintError(parseError(error.value ?? data.value, t('errors.load_data')))
        }
        appStore.stopLoading()
    }

    const onSelectUnit = (unit: Unit) => {
        appStore.setSelectedUnit(unit)
        open.value = false
    }

    const searchUnits = () => {
        if (searchUnitsValue.value.length === 0) {
            filteredUnits.value = units.value.slice(0, 10)
            return
        }
        if (searchUnitsTimeout) clearTimeout(searchUnitsTimeout)

        searchUnitsTimeout = setTimeout(() => {
            filteredUnits.value = units.value.filter(x => x.full_title && x.full_title.toLowerCase().indexOf(searchUnitsValue.value.toLowerCase()) !== -1)
        }, 200)
    }

    // watcher
    watch(open, (isOpen) => {
        if (isOpen) searchUnitsValue.value = ''
    })

    // hook
    onMounted(async() => {
        await loadData()
    })
</script>
